import { Injectable, Injector, Signal, inject, runInInjectionContext } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { EntityId } from '@ngrx/signals/entities';
import Computer from 'mbs-ui-shared@models/Computer';
import SummaryStat from 'mbs-ui-shared@models/rmm/SummaryStat';
import { SummaryComputer } from 'mbs-ui-shared@modules/summary-computers/store/summary-computer.model';
import { SummaryComputersStore } from 'mbs-ui-shared@modules/summary-computers/summary-computers.store';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SummaryComputersFacade {
  private store = inject(SummaryComputersStore);
  private injector = inject(Injector);

  readonly currentSystemTime$: Observable<SummaryStat['systemTime']> = toObservable(this.store.selectCurrentSystemTime);

  // ---------------------------------------------------------------------------
  // Computed signal pass-throughs
  // ---------------------------------------------------------------------------

  get currentHid(): Signal<string> {
    return this.store.selectCurrentHid;
  }

  get currentComputer(): Signal<Computer> {
    return this.store.selectCurrentComputer;
  }

  get current(): Signal<SummaryComputer> {
    return this.store.selectCurrent;
  }

  get loading(): Signal<boolean> {
    return this.store.selectLoading;
  }

  get access(): Signal<boolean> {
    return this.store.selectAccessState;
  }

  get currentSystemTime(): Signal<SummaryStat['systemTime']> {
    return this.store.selectCurrentSystemTime;
  }

  get all(): Signal<SummaryComputer[]> {
    return this.store.entities;
  }

  get entityMap(): Signal<Record<string, SummaryComputer>> {
    return this.store.entityMap as Signal<Record<string, SummaryComputer>>;
  }

  // ---------------------------------------------------------------------------
  // Parameterized selector factories — return computed signals
  // ---------------------------------------------------------------------------

  byHid(hid: string): Signal<SummaryComputer> {
    return this.store.selectByHid(hid);
  }

  loadingByHid(hid: string): Signal<boolean> {
    return this.store.selectLoadingByHid(hid);
  }

  diskByHid(hid: string): Signal<SummaryStat['hdd']> {
    return this.store.selectDiskByHid(hid);
  }

  cpuByHid(hid: string): Signal<SummaryStat['cpu']> {
    return this.store.selectCpuByHid(hid);
  }

  memoryByHid(hid: string): Signal<SummaryStat['ram']> {
    return this.store.selectMemoryByHid(hid);
  }

  antivirusByHid(hid: string): Signal<SummaryStat['antivirus']> {
    return this.store.selectAntivirusByHid(hid);
  }

  eventLogByHid(hid: string): Signal<SummaryStat['eventLog']> {
    return this.store.selectEventLogByHid(hid);
  }

  updateByHid(hid: string): Signal<SummaryStat['update']> {
    return this.store.selectUpdateByHid(hid);
  }

  alertsByHid(hid: string): Signal<SummaryStat['alerts']> {
    return this.store.selectAlertsByHid(hid);
  }

  smartByHid(hid: string): Signal<SummaryStat['smart']> {
    return this.store.selectSmartByHid(hid);
  }

  temperatureByHid(hid: string): Signal<SummaryStat['temperature']> {
    return this.store.selectTemperatureByHid(hid);
  }

  computerStateByHid(hid: string): Signal<SummaryStat['computerState']> {
    return this.store.selectComputerStateByHid(hid);
  }

  systemTimeByHid(hid: string): Signal<SummaryStat['systemTime']> {
    return this.store.selectSystemTimeByHid(hid);
  }

  systemTimeByHid$(hid: string): Observable<SummaryStat['systemTime']> {
    return runInInjectionContext(this.injector, () => toObservable(this.store.selectSystemTimeByHid(hid)));
  }

  appTimeDiffByHid(hid: string, unit: 'milliseconds' | 'seconds' | 'minutes' | 'hours' = 'hours'): Signal<number> {
    return this.store.selectAppTimeDiffByHid(hid, unit);
  }

  // ---------------------------------------------------------------------------
  // Commands — delegate to store
  // ---------------------------------------------------------------------------

  setCurrentComputer(computer: Computer): void {
    this.store.setCurrentComputer(computer);
  }

  loadSummaryComputer(params: { hid: string; force?: boolean; optional?: any }): void {
    this.store.loadSummaryComputer(params);
  }

  upsertSummaryComputer(summaryComputer: SummaryComputer): void {
    this.store.upsertSummaryComputer(summaryComputer);
  }

  upsertSummaryComputers(summaryComputers: SummaryComputer[]): void {
    this.store.upsertSummaryComputers(summaryComputers);
  }

  addSummaryComputer(summaryComputer: SummaryComputer): void {
    this.store.addSummaryComputer(summaryComputer);
  }

  addSummaryComputers(summaryComputers: SummaryComputer[]): void {
    this.store.addSummaryComputers(summaryComputers);
  }

  updateSummaryComputer(update: { id: EntityId; changes: Partial<SummaryComputer> }): void {
    this.store.updateSummaryComputer(update);
  }

  updateSummaryComputers(updates: { id: EntityId; changes: Partial<SummaryComputer> }[]): void {
    this.store.updateSummaryComputers(updates);
  }

  deleteSummaryComputer(id: string): void {
    this.store.deleteSummaryComputer(id);
  }

  deleteSummaryComputers(ids: string[]): void {
    this.store.deleteSummaryComputers(ids);
  }

  clearSummaryComputers(): void {
    this.store.clearSummaryComputers();
  }
}
